/**
 * Den Check-in in den Cloud-Ordner spiegeln.
 *
 * Das Gegenstück zu `datensync.js`, eine Datei daneben: Der Check-in ändert
 * sich selten und trägt Geheimtext, die Tauchgänge ändern sich oft. Getrennte
 * Dateien übertragen nur, was sich wirklich geändert hat.
 *
 * **Warum das hier steht und nicht in der Sync-Ansicht.** Es lag bisher als
 * Verschluss in `views/sync.js` und lief nur, wenn jemand diesen Reiter
 * aufschlug und einen Knopf drückte. Genau darauf ist kein Verlass: Wer den
 * Browserspeicher leert, hat den Knopf nie gedrückt. Ausgelagert kann derselbe
 * Weg von der Automatik und vom Check-in-Reiter aus gegangen werden.
 *
 * **Der Tresor wandert mit** — Salz, Rundenzahl und Prüfpaket. Er ist kein
 * Geheimnis: Ohne das Passwort ist er nutzlos, und ohne ihn könnte ein zweites
 * Gerät den Geheimtext nicht lesen, selbst mit richtigem Passwort.
 */

import { SPEICHER } from './db.js';
import { holeGrabsteine, holeTresor } from './checkin.js';
import { baueAbzug, fuehreZusammen, geraeteKennung } from './sync.js';

export const DATEINAME = 'tauchlogbuch-checkin.json';

function alleSaetze(db, speicher) {
  return new Promise((erfuellen, ablehnen) => {
    const anfrage = db.transaction(speicher, 'readonly').objectStore(speicher).getAll();
    anfrage.onsuccess = () => erfuellen(anfrage.result);
    anfrage.onerror = () => ablehnen(anfrage.error);
  });
}

export function schreibeSaetze(db, saetze, grabsteine, tresor) {
  return new Promise((erfuellen, ablehnen) => {
    const t = db.transaction([SPEICHER.checkin, SPEICHER.tresor], 'readwrite');
    const laden = t.objectStore(SPEICHER.checkin);
    laden.clear();
    for (const satz of saetze) laden.put(satz);
    if (tresor) t.objectStore(SPEICHER.tresor).put(tresor, 'tresor');
    t.objectStore(SPEICHER.tresor).put(grabsteine, 'grabsteine');
    t.oncomplete = () => erfuellen();
    t.onerror = () => ablehnen(t.error);
  });
}

/** Was auf diesem Gerät liegt — Geheimtext, Tresor, Grabsteine. */
export async function eigenerCheckinstand(db) {
  return {
    checkin: await alleSaetze(db, SPEICHER.checkin),
    tresor: await holeTresor(db),
    grabsteine: await holeGrabsteine(db),
  };
}

/**
 * Einen Abgleich durchführen.
 *
 * `stelleHer` macht aus dem gelesenen JSON wieder Bytes — die Adapter reichen
 * ihre eigene Fassung herein, damit dieses Modul nichts über Anbieter weiss.
 *
 * Schlägt das Lesen fehl, bleibt der lokale Bestand unangetastet.
 */
export async function gleicheCheckinAb(db, adapter, { stelleHer = (x) => x } = {}) {
  const fern = await adapter.lies();
  const lokal = await eigenerCheckinstand(db);

  if (!fern) {
    await adapter.schreib(baueAbzug({ ...lokal, geraet: geraeteKennung() }));
    return { bericht: null, erstmalig: true, anzahl: lokal.checkin.length };
  }

  const zusammen = fuehreZusammen(lokal, stelleHer(fern));
  await schreibeSaetze(db, zusammen.checkin, zusammen.grabsteine, zusammen.tresor);
  await adapter.schreib(baueAbzug({
    checkin: zusammen.checkin,
    tresor: zusammen.tresor,
    grabsteine: zusammen.grabsteine,
    geraet: geraeteKennung(),
  }));
  return { bericht: zusammen.bericht, erstmalig: false, anzahl: zusammen.checkin.length };
}
